/**
 * RFC 9457 `application/problem+json`.
 *
 * Clients switch on the stable `type` SUFFIX and translate it themselves. They never render
 * `detail` — it is English, developer-facing prose, and the buyer portal is Russian.
 */

/** One field-level validation error. `code` is stable and translatable — no prose. */
export interface ProblemFieldError {
  field: string;
  code: string;
}

/** A per-line selling-price diff, present on `price-changed`. Selling prices ONLY. */
export interface PriceChange {
  product_id: string;
  expected: string;
  current: string;
}

export interface ProblemBody {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  errors?: ProblemFieldError[];
  price_changes?: PriceChange[];
  unavailable_items?: string[];
  retry_after_seconds?: number;
}

/**
 * The stable problem `type` suffixes the SPAs branch on. The full type is a URI
 * (`https://most.example/problems/<suffix>`); only the suffix is contractual.
 */
export type ProblemType =
  | 'validation-failed'
  | 'auth-required'
  | 'forbidden'
  | 'not-found'
  | 'conflict'
  | 'rate-limited'
  | 'payload-too-large'
  | 'unsupported-media-type'
  | 'price-changed'
  | 'cart-item-unavailable'
  | 'unknown';

export class ProblemError extends Error {
  /** The `type` SUFFIX, e.g. `price-changed`. Never the full URI. */
  readonly type: ProblemType | string;
  readonly status: number;
  readonly title: string;
  readonly errors: ProblemFieldError[];
  /** Populated on 409 `price-changed`. */
  readonly priceChanges: PriceChange[];
  /** Populated on 409 `cart-item-unavailable` — product ids to flag red in the cart. */
  readonly unavailableItems: string[];
  readonly retryAfterSeconds: number | null;
  readonly body: ProblemBody;

  constructor(status: number, body: ProblemBody) {
    // `detail` is English and never displayed; it is only here so the Error is debuggable.
    super(body.detail ?? body.title ?? `HTTP ${status}`);
    this.name = 'ProblemError';
    this.status = body.status ?? status;
    this.type = extractProblemType(body.type);
    this.title = body.title ?? '';
    this.errors = body.errors ?? [];
    this.priceChanges = body.price_changes ?? [];
    this.unavailableItems = body.unavailable_items ?? [];
    this.retryAfterSeconds = body.retry_after_seconds ?? null;
    this.body = body;
  }

  /** Map server field errors onto Formik's `setErrors` shape via a code→message translator. */
  fieldErrors(translate: (code: string, field: string) => string): Record<string, string> {
    const out: Record<string, string> = {};
    for (const e of this.errors) out[e.field] = translate(e.code, e.field);
    return out;
  }
}

/** `https://most.example/problems/price-changed` → `price-changed`. */
export function extractProblemType(type: string | undefined): string {
  if (!type) return 'unknown';
  const trimmed = type.replace(/\/+$/, '');
  const slash = trimmed.lastIndexOf('/');
  return slash === -1 ? trimmed : trimmed.slice(slash + 1);
}

export function isProblemError(e: unknown): e is ProblemError {
  return e instanceof ProblemError;
}

export function isProblemType(e: unknown, type: ProblemType): e is ProblemError {
  return isProblemError(e) && e.type === type;
}
